import {
  EntityRefUtil,
  formatDateWithUserTimezone,
  formatTimeWithUserTimezone,
  FormElementKind,
  GetInspectionsRequestDTO,
  GetInspectionsResponseDTO,
  GetUnitsResponseDTO,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';

import WarningIcon from '@/components/icons/warning';
import { ASSET_SECTION_LABEL } from '@/components/table-page/columns/asset-tags-column';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';

const columnHelper = createColumnHelper<GetInspectionsResponseDTO['data'][number]>();

export const columns = (units?: GetUnitsResponseDTO['data']): AccessorKeyColumnDef<GetInspectionsResponseDTO['data'][number]>[] => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.display({
      id: 'asset',
      header: 'Asset',
      cell: ({ row }) => {
        const value = row.original.asset;
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: { columnGroup: ASSET_SECTION_LABEL },
    }),
    columnHelper.accessor((row) => formatDateWithUserTimezone(new Date(row.inspectionDate), userTimezone), {
      id: 'createdDate',
      header: 'Created Date',
      enableSorting: true,
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'date-range',
      },
    }),
    columnHelper.accessor((row) => formatTimeWithUserTimezone(new Date(row.inspectionDate), userTimezone), {
      id: 'createdTime',
      header: 'Created Time',
      enableSorting: true,
      enableGrouping: false,
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value.name}</span> : <span className="text-secondary-text">-</span>;
      },
      getGroupingValue: (row) => row.location?.name || 'None',
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('inspectedBy', {
      header: 'Created By',
      cell: ({ row }) => {
        const inspection = row.original;
        const fullName = EntityRefUtil.createUserFullNameRef(inspection.inspectedBy);
        const value = fullName || '-';
        const avatarBackgroundColorClass = getAvatarBackgroundColorClass(inspection.inspectedBy?.firstName || '');

        const inspectedByFullInitials = initials(fullName);
        return (
          <UserAvatarLabel
            value={value}
            operatedByFullInitials={inspectedByFullInitials}
            avatarUrl={inspection.inspectedBy?.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBackgroundColorClass}
          />
        );
      },
      getGroupingValue: (row) => EntityRefUtil.createUserFullNameRef(row.inspectedBy, 'None'),
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })).json();

          const resultsMap = new Map(data.map((user) => [user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (
                await ApiService.GetUsers({
                  limit: 100,
                  filters: { id: missingIds, userType: 'sitemate-users' },
                })
              ).json();

              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.form?.name, {
      id: 'formName',
      header: 'Form Name',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
    }),
    columnHelper.accessor((row) => row.form?.number, {
      id: 'formNumber',
      header: 'Form ID',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createFormRef(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        filterType: 'text',
      },
      enableColumnFilter: true,
      enableGrouping: true,
    }),
    columnHelper.accessor('issueCount', {
      header: 'Issues',
      enableGrouping: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? (
          <span className="bg-default-light-orange text-default-dark-orange flex w-fit rounded-[5px] px-[5px] py-[2px]">
            <WarningIcon />
            {value}
          </span>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
    }),
    ...(units
      ? units.map((unit) =>
          columnHelper.display({
            id: unit.id,
            header: `Reading ${unit.abbreviation}`,
            enableGrouping: false,
            cell: ({ row }) => {
              const element = row.original.formRevision?.elements?.find(
                (element) => element.kind === FormElementKind.UnitReading && element.config?.unitId === unit.id,
              );
              const reading = row.original.data?.find((answer) => answer.elementId === element?.id)?.value;
              return reading ? (
                <span>
                  {reading?.toLocaleString('en-US', { maximumFractionDigits: 0 })} <span className="text-secondary-text">{unit?.abbreviation}</span>
                </span>
              ) : (
                <span className="text-secondary-text">-</span>
              );
            },
          }),
        )
      : []),
    columnHelper.accessor('number', {
      id: 'number',
      header: 'Inspection Number',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createInspectionRef(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: false,
    }),
  ] as AccessorKeyColumnDef<GetInspectionsResponseDTO['data'][number]>[];
};

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...columns()
    .filter((column) => {
      const availableGroupColumns = ['location', 'inspectedBy', 'createdDate', 'formName', 'formNumber'];
      return !column.accessorKey && column.id ? availableGroupColumns.includes(column.id) : availableGroupColumns.includes(column.accessorKey);
    })
    .map((column) => ({ label: column.header?.toString() || '', accessor: !column.accessorKey && column.id ? column.id : column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof columns>[number]['id'] | ReturnType<typeof columns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetInspectionsRequestDTO['filters']>['fields']>
> = {
  location: 'locationId',
  inspectedBy: 'inspectedById',
  formName: 'formName',
  formNumber: 'formNumber',
  createdDate: 'createdDate',
};
